
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import {
  charactersApi,
  type CharactersListParams,
  type PageParams,
} from '../api/endpoints';
import type {
  Character,
  CharacterCreateRequest,
  CharacterJob,
  CharacterUpdateRequest,
  Id,
  MediaLink,
  OkResponse,
  Page,
} from '../types/api';
import { queryKeys } from './keys';
import { renewDelayMs } from './useMedia';

export function useCharacters(
  params: CharactersListParams = {},
): UseQueryResult<Page<Character>, ApiError> {
  return useQuery<Page<Character>, ApiError>({
    queryKey: queryKeys.characters(params),
    queryFn: () => charactersApi.list(params),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useReadyCharacters(limit = 200): UseQueryResult<Page<Character>, ApiError> {
  return useCharacters({ ready: true, is_active: true, limit });
}

export function useCharacter(characterId: Id | undefined): UseQueryResult<Character, ApiError> {
  return useQuery<Character, ApiError>({
    queryKey: queryKeys.character(characterId ?? ''),
    queryFn: () => charactersApi.get(characterId as Id),
    enabled: Boolean(characterId),
    staleTime: 60_000,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

export function useCharacterAvatarUrl(
  characterId: Id | undefined,
  enabled = true,
): UseQueryResult<MediaLink, ApiError> {
  return useQuery<MediaLink, ApiError>({
    queryKey: queryKeys.characterAvatarUrl(characterId ?? ''),
    queryFn: () => charactersApi.avatarUrl(characterId as Id),
    enabled: Boolean(characterId) && enabled,
    staleTime: Infinity,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => renewDelayMs(query.state.data?.expires_at),
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

export function useCharacterJobs(
  characterId: Id | undefined,
  params: PageParams = {},
): UseQueryResult<Page<CharacterJob>, ApiError> {
  return useQuery<Page<CharacterJob>, ApiError>({
    queryKey: queryKeys.characterJobs(characterId ?? '', params),
    queryFn: () => charactersApi.jobs(characterId as Id, params),
    enabled: Boolean(characterId),
    placeholderData: keepPreviousData,
  });
}

export function useCreateCharacter() {
  const qc = useQueryClient();
  return useMutation<Character, ApiError, CharacterCreateRequest>({
    mutationFn: (payload) => charactersApi.create(payload),
    onSuccess: (character) => {
      qc.setQueryData(queryKeys.character(character.id), character);
      void qc.invalidateQueries({ queryKey: queryKeys.charactersRoot });
    },
  });
}

export function useUpdateCharacter(characterId: Id) {
  const qc = useQueryClient();
  return useMutation<Character, ApiError, CharacterUpdateRequest>({
    mutationFn: (payload) => charactersApi.update(characterId, payload),
    onSuccess: (character) => {
      qc.setQueryData(queryKeys.character(characterId), character);
      void qc.invalidateQueries({ queryKey: queryKeys.charactersRoot });
    },
  });
}

export function useDeleteCharacter() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiError, Id>({
    mutationFn: (characterId) => charactersApi.remove(characterId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.charactersRoot });
      void qc.invalidateQueries({ queryKey: queryKeys.voicesRoot });
    },
  });
}

export function useUploadAvatar(characterId: Id) {
  const qc = useQueryClient();
  return useMutation<Character, ApiError, File>({
    mutationFn: (file) => charactersApi.uploadAvatar(characterId, file),
    retry: false,
    onSuccess: (character) => {
      qc.setQueryData(queryKeys.character(characterId), character);
      qc.removeQueries({ queryKey: queryKeys.characterAvatarUrl(characterId) });
      void qc.invalidateQueries({ queryKey: queryKeys.charactersRoot });
    },
  });
}

export const MISSING_LABELS: Readonly<Record<string, string>> = {
  avatar: 'нет аватара',
  voice: 'нет голоса',
  video: 'нет исходного видео',
};

export function describeMissing(character: Character | undefined): string {
  const missing = character?.missing ?? [];
  if (missing.length === 0) return '';
  return missing.map((item) => MISSING_LABELS[item] ?? item).join(', ');
}

export function currentVoiceId(character: Character | undefined): Id | null {
  return character?.current_voice?.voice_profile_id ?? null;
}
